/**
 * Copyright (c) 2026 PhiloFund. All rights reserved.
 *
 * GlobalSearch.tsx — site-wide search dialog (Cmd/Ctrl+K or AppBar button).
 *
 * Role-aware sources — results are strictly scoped to what the current role
 * is allowed to see (PRD-021 segregation of duties):
 *   - everyone:        organizations (GET /api/organizations?search=)
 *   - org_admin:       + their tenant's donations (GET /api/donations?search=)
 *   - platform_admin:  + cross-tenant donor search (GET /api/admin/donors?search=)
 *
 * Selecting a result navigates to the role-appropriate page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api.client';
import { COLOR } from '@/theme/tokens';
import type { Organization } from '@philofund/shared/types/organization';
import type { Donation } from '@philofund/shared/types/donation';
import type { UserRole } from '@philofund/shared/types/user';

interface Envelope<T> {
  success: true;
  data: T;
}

interface AdminDonorHit {
  id: string;
  name: string;
  email: string;
  totalDonatedCents: number;
  donationCount: number;
}

export interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
  role: UserRole | undefined;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

export function GlobalSearch({ open, onClose, role }: GlobalSearchProps): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  // 300ms debounce so we don't hammer the API per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(query.trim()); }, 300);
    return () => { clearTimeout(t); };
  }, [query]);

  // Reset on close so the dialog opens clean next time.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
    }
  }, [open]);

  const enabled = open && debounced.length >= 2;

  const orgResults = useQuery({
    queryKey: ['global-search', 'orgs', debounced],
    queryFn: async () => {
      const res = await apiClient.get<Envelope<Organization[]>>(
        `/api/organizations?search=${encodeURIComponent(debounced)}&limit=5`,
      );
      return res.data.data;
    },
    enabled,
  });

  const donationResults = useQuery({
    queryKey: ['global-search', 'donations', debounced],
    queryFn: async () => {
      const res = await apiClient.get<Envelope<Donation[]>>(
        `/api/donations?search=${encodeURIComponent(debounced)}&limit=5`,
      );
      return res.data.data;
    },
    enabled: enabled && role === 'org_admin',
  });

  const donorResults = useQuery({
    queryKey: ['global-search', 'donors', debounced],
    queryFn: async () => {
      const res = await apiClient.get<Envelope<AdminDonorHit[]>>(
        `/api/admin/donors?search=${encodeURIComponent(debounced)}`,
      );
      return res.data.data;
    },
    enabled: enabled && role === 'platform_admin',
  });

  const isLoading =
    orgResults.isFetching || donationResults.isFetching || donorResults.isFetching;

  const go = useCallback(
    (path: string) => {
      onClose();
      navigate(path);
    },
    [navigate, onClose],
  );

  const hasAnyResult = useMemo(() => {
    return (
      (orgResults.data?.length ?? 0) > 0 ||
      (donationResults.data?.length ?? 0) > 0 ||
      (donorResults.data?.length ?? 0) > 0
    );
  }, [orgResults.data, donationResults.data, donorResults.data]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: 2 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search nonprofits…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {isLoading ? <CircularProgress size={18} /> : <SearchIcon />}
                </InputAdornment>
              ),
            },
          }}
          aria-label="Site-wide search"
        />

        {enabled && !isLoading && !hasAnyResult && (
          <Typography variant="body2" sx={{ color: COLOR.text.secondary, mt: 2, textAlign: 'center' }}>
            No results for “{debounced}”.
          </Typography>
        )}

        <List dense>
          {(orgResults.data?.length ?? 0) > 0 && (
            <>
              <ListSubheader disableSticky>Nonprofits</ListSubheader>
              {orgResults.data?.map((org) => (
                <ListItemButton key={org.id} onClick={() => { go(`/organizations/${org.id}`); }}>
                  <ListItemIcon><BusinessIcon sx={{ color: COLOR.secondary.main }} /></ListItemIcon>
                  <ListItemText
                    primary={org.name}
                    secondary={`${org.address.city}, ${org.address.state}`}
                  />
                </ListItemButton>
              ))}
            </>
          )}

          {role === 'org_admin' && (donationResults.data?.length ?? 0) > 0 && (
            <>
              <ListSubheader disableSticky>Donations received</ListSubheader>
              {donationResults.data?.map((d) => (
                <ListItemButton key={d.id} onClick={() => { go('/donations-received'); }}>
                  <ListItemIcon><AttachMoneyIcon sx={{ color: COLOR.primary.main }} /></ListItemIcon>
                  <ListItemText
                    primary={formatCents(d.amountCents)}
                    secondary={new Date(d.createdAt).toLocaleDateString()}
                  />
                  <Chip label={d.status} size="small" />
                </ListItemButton>
              ))}
            </>
          )}

          {role === 'platform_admin' && (donorResults.data?.length ?? 0) > 0 && (
            <>
              <ListSubheader disableSticky>Donors (platform)</ListSubheader>
              {donorResults.data?.map((donor) => (
                <ListItemButton key={donor.id} onClick={() => { go('/admin/donors'); }}>
                  <ListItemIcon><PersonSearchIcon sx={{ color: COLOR.info.main }} /></ListItemIcon>
                  <ListItemText
                    primary={donor.name}
                    secondary={`${donor.email} · ${donor.donationCount} gifts · ${formatCents(donor.totalDonatedCents)}`}
                  />
                </ListItemButton>
              ))}
            </>
          )}
        </List>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Typography variant="caption" sx={{ color: COLOR.text.secondary }}>
            Results are scoped to your role’s permissions.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
